import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Panel } from '../../components/primitives.tsx';
import { PortalHelpLink } from '../help/PortalHelpLink.tsx';
import { usePatientSession } from '../session/PatientSessionContext.ts';

/**
 * Portal home — the landing page after sign-in, and `resolvePortalRedirectTarget`'s own default
 * destination. A simple set of links into the three built pillars (records, scheduling, messaging)
 * rather than a dashboard aggregating live data from all three: this task is scoped to standing up
 * the base portal, not a summary widget on top of it — `docs/decisions/patient-portal-frontend-
 * scope.md`.
 */
export function PortalHomePage() {
  const { t } = useTranslation('portalHome');
  const { orgId } = useParams<{ orgId: string }>();
  const session = usePatientSession();

  if (orgId === undefined) return null;

  return (
    <div className="flex flex-col gap-4">
      <h1 className="m-0 text-xl font-semibold text-ink">
        {t('greeting', { name: session.firstName })}
      </h1>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <HomeCard to={`/portal/${orgId}/records`} title={t('cards.records.title')}>
          {t('cards.records.body')}
        </HomeCard>
        <HomeCard
          to={`/portal/${orgId}/schedule`}
          title={t('cards.schedule.title')}
          helpTopicId="booking-an-appointment"
        >
          {t('cards.schedule.body')}
        </HomeCard>
        <HomeCard to={`/portal/${orgId}/messages`} title={t('cards.messages.title')}>
          {t('cards.messages.body')}
        </HomeCard>
      </div>
    </div>
  );
}

function HomeCard({
  to,
  title,
  helpTopicId,
  children,
}: {
  readonly to: string;
  readonly title: string;
  /** Optional, per-card `PortalHelpLink` (`BACKLOG.md` W6). Only the schedule card below passes
   * one — records and messages each carry their own `PortalHelpLink` on their own page's heading
   * already (`PortalRecordsPage.tsx`, `PortalMessagesPage.tsx`), so putting one here too would be
   * the exact "scattered on every field" invariants.md warns against. Schedule is the one exception:
   * `PortalSchedulePage.tsx` itself is under concurrent edit by another task (`BACKLOG.md` W5, an
   * appointments list landing on that same file), so its own help affordance lives here instead of
   * risking a collision by touching that file directly. */
  readonly helpTopicId?: string | undefined;
  readonly children: string;
}) {
  return (
    <Panel
      title={
        helpTopicId === undefined ? (
          title
        ) : (
          <>
            {title}
            <PortalHelpLink topicId={helpTopicId} />
          </>
        )
      }
    >
      <p className="m-0 mb-3 text-sm text-ink-muted">{children}</p>
      <Link
        to={to}
        className="text-sm font-medium text-accent-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
      >
        {title} →
      </Link>
    </Panel>
  );
}
